import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, ErrorState, LoadingState } from '@/shared/ui';

import { useApplyPriceChangeBatch, useCustomerGroups, useDryRunPriceChangeBatch, usePriceChangeBatch, usePriceLists, useSalesChannels } from '../../hooks';
import { formatPrice } from '../../lib/format';
import { batchItemOutcomeColor, batchStatusColor, BATCH_ITEM_OUTCOME_LABELS, BATCH_STATUS_LABELS } from '../../model/options';
import type { BatchItemOutcome, PriceChangeBatchItemResponse } from '../../model/priceBatchTypes';
import type { ToastSeverity } from '../../lib/useToast';

export interface PriceChangeBatchDetailViewProps {
  batchId: string;
  canBulk: boolean;
  onBack: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const OUTCOME_FILTERS: BatchItemOutcome[] = ['OK', 'REQUIRES_APPROVAL', 'BLOCKED', 'ERROR'];

function adjustmentLabel(type: string, value: number): string {
  if (type === 'PERCENT') return `${value > 0 ? '+' : ''}${value}%`;
  if (type === 'ABSOLUTE') return `${value > 0 ? '+' : ''}${formatPrice(value)}`;
  return `= ${formatPrice(value)}`;
}

/** Batch detail — the dry-run diff table through to apply (design spec §8.8). Rendered INLINE by
 * `PriceChangeBatchesPanel` (no route: batches/plans stay tab-local state, unlike price lists'
 * routed detail page) so the "back" affordance simply clears the panel's local selection. */
export function PriceChangeBatchDetailView({ batchId, canBulk, onBack, onResult }: PriceChangeBatchDetailViewProps) {
  const { t } = useTranslation('catalog');
  const [outcomeFilter, setOutcomeFilter] = useState<BatchItemOutcome | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);

  const detailQuery = usePriceChangeBatch(batchId);
  const dryRunMutation = useDryRunPriceChangeBatch();
  const applyMutation = useApplyPriceChangeBatch();
  const channelsQuery = useSalesChannels();
  const listsQuery = usePriceLists();
  const groupsQuery = useCustomerGroups();

  if (detailQuery.isLoading) {
    return <LoadingState label={t('bulkBatchesPanel.loading')} />;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorState message={detailQuery.error?.message} onRetry={() => void detailQuery.refetch()} />;
  }

  const { batch, items } = detailQuery.data;

  const targetChip = batch.targetPriceListCode
    ? t('bulkBatchesPanel.target.listChip', {
        name: (listsQuery.data ?? []).find((l) => l.code === batch.targetPriceListCode)?.name ?? batch.targetPriceListCode,
      })
    : batch.targetCustomerGroupCode
      ? t('bulkBatchesPanel.target.groupChip', {
          name: (groupsQuery.data ?? []).find((g) => g.code === batch.targetCustomerGroupCode)?.name ?? batch.targetCustomerGroupCode,
        })
      : batch.targetChannelCode
        ? ((channelsQuery.data ?? []).find((c) => c.code === batch.targetChannelCode)?.name ?? batch.targetChannelCode)
        : t('bulkBatchesPanel.target.base');

  const counts = {
    OK: batch.itemsOk ?? items.filter((i) => i.outcome === 'OK').length,
    REQUIRES_APPROVAL: batch.itemsRequiresApproval ?? items.filter((i) => i.outcome === 'REQUIRES_APPROVAL').length,
    BLOCKED: batch.itemsBlocked ?? items.filter((i) => i.outcome === 'BLOCKED').length,
    ERROR: batch.itemsError ?? items.filter((i) => i.outcome === 'ERROR').length,
  };

  const filteredItems = outcomeFilter ? items.filter((i) => i.outcome === outcomeFilter) : items;
  const pageItems = filteredItems.slice(page * size, page * size + size);

  const canApply = canBulk && batch.status === 'DRY_RUN_READY';
  const canDryRun = canBulk && (batch.status === 'DRAFT' || batch.status === 'FAILED');

  const handleDryRun = () => {
    dryRunMutation.mutate(batch.id, {
      onError: (error) => onResult('error', error.message),
    });
  };

  const handleApply = () => {
    applyMutation.mutate(batch.id, {
      onSuccess: (detail) => {
        setApplyConfirmOpen(false);
        const approval = detail.batch.itemsRequiresApproval ?? 0;
        onResult(
          'success',
          approval > 0
            ? t('bulkBatchesPanel.appliedWithApprovals', { count: approval })
            : t('bulkBatchesPanel.applied'),
        );
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>
            {t('bulkBatchesPanel.backAction')}
          </Button>
          <Typography variant="subtitle1" fontWeight={700}>
            {batch.name || batch.id}
          </Typography>
          <Chip size="small" variant="outlined" label={targetChip} />
          <Chip size="small" variant="outlined" label={adjustmentLabel(batch.adjustmentType, batch.adjustmentValue)} />
          <Chip size="small" color={batchStatusColor(batch.status)} label={t(BATCH_STATUS_LABELS[batch.status] ?? batch.status)} />
        </Stack>
        <Stack direction="row" spacing={1}>
          {canDryRun ? (
            <Button variant="outlined" onClick={handleDryRun} disabled={dryRunMutation.isPending} startIcon={dryRunMutation.isPending ? <CircularProgress size={16} /> : undefined}>
              {t('bulkBatchesPanel.dryRunAction')}
            </Button>
          ) : null}
          {batch.status === 'DRY_RUN_READY' ? (
            <Tooltip title={!canBulk ? t('bulkBatchesPanel.noBulkPermission') : ''} disableHoverListener={canBulk}>
              <span>
                <Button variant="contained" disabled={!canApply} onClick={() => setApplyConfirmOpen(true)}>
                  {t('common:action.apply')} ({counts.OK + counts.REQUIRES_APPROVAL})
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      {batch.status === 'FAILED' ? <Alert severity="error">{t('bulkBatchesPanel.failedMessage')}</Alert> : null}
      {batch.status === 'APPLYING' ? <Alert severity="info" icon={<CircularProgress size={16} />}>{t('bulkBatchesPanel.applyingMessage')}</Alert> : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {OUTCOME_FILTERS.map((outcome) => (
          <Chip
            key={outcome}
            clickable
            size="small"
            color={outcomeFilter === outcome ? batchItemOutcomeColor(outcome) : 'default'}
            variant={outcomeFilter === outcome ? 'filled' : 'outlined'}
            label={`${t(BATCH_ITEM_OUTCOME_LABELS[outcome])}: ${counts[outcome]}`}
            onClick={() => {
              setOutcomeFilter((current) => (current === outcome ? null : outcome));
              setPage(0);
            }}
          />
        ))}
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('bulkBatchesPanel.diff.product')}</TableCell>
              <TableCell align="right">{t('bulkBatchesPanel.diff.current')}</TableCell>
              <TableCell align="right">{t('bulkBatchesPanel.diff.next')}</TableCell>
              <TableCell align="right">{t('bulkBatchesPanel.diff.delta')}</TableCell>
              <TableCell>{t('bulkBatchesPanel.diff.outcome')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageItems.map((item: PriceChangeBatchItemResponse) => {
              const delta = item.newAmount !== undefined && item.oldAmount !== undefined ? item.newAmount - item.oldAmount : undefined;
              return (
                <TableRow key={item.productId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                      {item.productId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {item.oldAmount !== undefined ? formatPrice(item.oldAmount) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {item.newAmount !== undefined ? formatPrice(item.newAmount) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color={delta !== undefined && delta < 0 ? 'success.main' : undefined}>
                      {delta !== undefined ? `${delta > 0 ? '+' : ''}${formatPrice(delta)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={item.errorMessage ?? ''} disableHoverListener={!item.errorMessage}>
                      <Chip
                        size="small"
                        color={batchItemOutcomeColor(item.outcome)}
                        variant={item.outcome === 'ERROR' ? 'outlined' : 'filled'}
                        label={item.outcome ? t(BATCH_ITEM_OUTCOME_LABELS[item.outcome] ?? item.outcome) : '—'}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CompactTablePagination
        count={filteredItems.length}
        page={page}
        rowsPerPage={size}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(_e, next) => setPage(next)}
        onRowsPerPageChange={(e) => {
          setSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {counts.REQUIRES_APPROVAL > 0 && batch.status === 'APPLIED' ? (
        <MuiLink component={RouterLink} to="/catalog/pricing?tab=approvals">
          {t('bulkBatchesPanel.openApprovalsLink')}
        </MuiLink>
      ) : null}

      <Dialog open={applyConfirmOpen} onClose={applyMutation.isPending ? undefined : () => setApplyConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('bulkBatchesPanel.applyConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('bulkBatchesPanel.applyConfirm', { ok: counts.OK, approval: counts.REQUIRES_APPROVAL, blocked: counts.BLOCKED })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyConfirmOpen(false)} disabled={applyMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={applyMutation.isPending}
            startIcon={applyMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('common:action.apply')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
